import { api } from "@/api/client";
import { useAuth } from "@/store/auth";
/**
 * Опция для вызовов, которые НЕ должны запускать auto-refresh на 401.
 * Это login/logout/refresh/mfa — они «собственники» своей ошибки: если
 * login вернул 401, это «неверный пароль», а не «сессия истекла».
 */
const noRefresh = { _noRefresh: true };
function storeTokens(t) {
    useAuth.getState().setTokens({
        accessToken: t.access_token,
        accessExpiresAt: t.access_expires_at,
    });
}
export async function login(email, password, tenantSlug) {
    const body = {
        email,
        password,
    };
    if (tenantSlug)
        body.tenant_slug = tenantSlug;
    const r = await api.post("/auth/login", body, noRefresh);
    if (!r.data.mfa_required) {
        storeTokens(r.data);
    }
    return r.data;
}
export async function loginMfa(mfaSessionToken, totpCode) {
    const r = await api.post("/auth/login/mfa", {
        mfa_session_token: mfaSessionToken,
        totp_code: totpCode,
    }, noRefresh);
    storeTokens(r.data);
    return r.data;
}
export async function refresh() {
    const r = await api.post("/auth/refresh", undefined, noRefresh);
    storeTokens(r.data);
    return r.data;
}
/**
 * Logout. Помимо чистки auth-store обнуляет TanStack Query cache —
 * иначе после re-login другого пользователя в `/me` мог бы блеснуть
 * предыдущий profile из кэша до первого refetch'а.
 *
 * Это осознанный выход (единственный вызыватель — AppLayout::handleLogout):
 * шлёт `everywhere` → backend каскадит SLO на продуктовые сессии браузера.
 * 401-interceptor сюда НЕ ходит — он чистит auth-store напрямую (api/client.ts).
 * `queryClient` принимаем явно (caller пробрасывает `useQueryClient()`).
 */
export async function logout(queryClient) {
    try {
        // everywhere: SLO-каскад — выход из дашборда гасит и продуктовые сессии
        // этого браузера (NET/Sonar/…), не только сессию самого auth.
        await api.post("/auth/logout", { everywhere: true }, noRefresh);
    }
    finally {
        useAuth.getState().clear();
        queryClient?.clear();
    }
}
export async function me() {
    const r = await api.get("/auth/me");
    return r.data;
}
/**
 * Правка своих имени и телефона. 204 — тело не нужно: вызывающий инвалидирует
 * ключ ["me"], как это делает загрузка аватара.
 *
 * Поля, которых нет в объекте, бэкенд не трогает; `phone: null` стирает телефон.
 * Поэтому НЕ подставляйте undefined-поля «для полноты» — тристейт значимый.
 */
export async function updateMe(patch) {
    await api.patch("/auth/me", patch);
}
/** Запрос смены адреса: письмо-подтверждение на новый, сигнал — на старый. */
export async function requestEmailChange(newEmail, currentPassword, totpCode) {
    const body = {
        new_email: newEmail,
        current_password: currentPassword,
    };
    if (totpCode)
        body.totp_code = totpCode;
    const r = await api.post("/auth/me/email", body);
    return r.data.pending_email;
}
/**
 * Подтвердить смену адреса КОДОМ из письма на новый адрес. Ручка под сессией
 * (Bearer): посторонний, получивший письмо по опечатке, ввести код не сможет.
 * Успех гасит все сессии, включая эту — вызывающий уводит на /login.
 */
export async function confirmEmailChangeCode(code) {
    const r = await api.post("/auth/me/email/confirm", { code });
    return r.data.email;
}
export async function changePassword(currentPassword, newPassword) {
    const r = await api.post("/auth/me/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
    });
    storeTokens(r.data);
    return r.data;
}
export async function mfaSetup() {
    const r = await api.post("/auth/me/mfa/setup");
    return r.data;
}
export async function mfaEnable(totpCode) {
    await api.post("/auth/me/mfa/enable", { totp_code: totpCode });
}
export async function mfaDisable(password, totpCode) {
    await api.post("/auth/me/mfa/disable", { password, totp_code: totpCode });
}
export async function listSessions() {
    const r = await api.get("/auth/me/sessions");
    return r.data.items;
}
export async function revokeSession(id) {
    await api.delete(`/auth/me/sessions/${id}`);
}
/** Завершить браузерную сессию целиком (SLO-каскад: ЛК + продукты). */
export async function revokeSsoSessionGroup(sid) {
    await api.delete(`/auth/me/sessions/sso/${sid}`);
}
export async function revokeOtherSessions() {
    const r = await api.post("/auth/me/sessions/revoke-others");
    return r.data.revoked;
}
/**
 * URL аватара по employee_id. Возвращает undefined, если фото нет
 * (updatedAt пустой) — тогда <img> не рендерится и 404 на сервер не уходит.
 * `?v=` — cache-bust своего же <img> после загрузки (сервер параметр игнорирует).
 */
export function avatarUrl(id, updatedAt) {
    if (!updatedAt)
        return undefined;
    return `/api/avatars/${id}?v=${encodeURIComponent(updatedAt)}`;
}
export async function uploadAvatar(file) {
    const fd = new FormData();
    fd.append("file", file);
    // Content-Type (multipart boundary) axios проставит сам — не задаём вручную.
    // 60 с вместо общих 15: фото с телефона по мобильному каналу не успевает,
    // и человек видит «Не удалось загрузить фото» на исправном файле.
    const r = await api.post("/auth/me/avatar", fd, {
        timeout: 60_000,
    });
    return r.data;
}
export async function deleteAvatar() {
    await api.delete("/auth/me/avatar");
}
export async function uploadEmployeeAvatar(employeeId, file) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`/admin/employees/${employeeId}/avatar`, fd);
    return r.data;
}
export async function deleteEmployeeAvatar(employeeId) {
    await api.delete(`/admin/employees/${employeeId}/avatar`);
}
